import * as React from 'react';
import {
  Box,
  Button,
  Container,
  Grid2 as Grid,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';

// Define the types for our form values
interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  role: string;
  message: string;
}

// Initial form state
const initialFormValues: FormValues = {
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  role: '',
  message: '',
};

const ContactSection = React.forwardRef<HTMLDivElement>((props, ref) => {
  const [formValues, setFormValues] =
    React.useState<FormValues>(initialFormValues);
  const [errors, setErrors] = React.useState<Partial<FormValues>>({});
  const [loading, setLoading] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<
    'success' | 'error'
  >('success');

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
    // Clear error when user types
    if (errors[name as keyof FormValues]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  // Validate the form
  const validateForm = (): boolean => {
    const newErrors: Partial<FormValues> = {};
    let isValid = true;

    // Validate first name
    if (!formValues.first_name.trim()) {
      newErrors.first_name = 'First name is required';
      isValid = false;
    }

    // Validate last name
    if (!formValues.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
      isValid = false;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formValues.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!emailRegex.test(formValues.email)) {
      newErrors.email = 'Invalid email format';
      isValid = false;
    }

    // Validate user type
    if (!formValues.role) {
      newErrors.role = 'Please select the role that best describes you';
      isValid = false;
    }

    // Validate message
    if (!formValues.message.trim()) {
      newErrors.message = 'Message is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true);

      // In a real app, replace with your API endpoint
      axios
        .post(process.env.REACT_APP_CONTACT_FORM_ENDPOINT || '', formValues)
        .then((response) => {
          setSnackbarSeverity('success');
          setSnackbarMessage(
            "Your message has been sent successfully! We'll be in touch soon.",
          );
          setSnackbarOpen(true);
          setFormValues(initialFormValues);
        })
        .catch((error) => {
          console.error('Error submitting form:', error);
          setSnackbarSeverity('error');
          setSnackbarMessage(
            'There was an error sending your message. Please try again later.',
          );
          setSnackbarOpen(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 6, md: 10 },
        bgcolor: 'background.paper',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h4"
            component="h2"
            fontWeight={600}
            sx={{ mb: 2 }}
          >
            Get In Touch
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{ maxWidth: 800, mx: 'auto', color: 'text.secondary' }}
          >
            Have questions about ClassAct? Interested in early access? Let us
            know!
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box>
              <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
                Contact Information
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, color: 'text.secondary' }}
                >
                  Have a question? Reach out to our team and we'll get back to
                  you as soon as possible.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      p: 1,
                      borderRadius: '50%',
                      mr: 2,
                      display: 'flex',
                    }}
                  >
                    <EmailIcon />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      component="a"
                      href="mailto:info@classact.com"
                      sx={{
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: 'primary.main',
                        },
                      }}
                    >
                      info@classact.com
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      p: 1,
                      borderRadius: '50%',
                      mr: 2,
                      display: 'flex',
                    }}
                  >
                    <PhoneIcon />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      component="a"
                      href="tel:+16316551144"
                      sx={{
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: 'primary.main',
                        },
                      }}
                    >
                      (631) 655-1144
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              }}
            >
              <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
                Send us a message
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="first_name"
                      value={formValues.first_name}
                      onChange={handleInputChange}
                      error={!!errors.first_name}
                      helperText={errors.first_name}
                      margin="normal"
                      variant="outlined"
                      required
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="last_name"
                      value={formValues.last_name}
                      onChange={handleInputChange}
                      error={!!errors.last_name}
                      helperText={errors.last_name}
                      margin="normal"
                      variant="outlined"
                      required
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formValues.email}
                      onChange={handleInputChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      margin="normal"
                      variant="outlined"
                      required
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone (optional)"
                      name="phone_number"
                      value={formValues.phone_number}
                      onChange={handleInputChange}
                      margin="normal"
                      variant="outlined"
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      select
                      label="I am a..."
                      name="role"
                      value={formValues.role}
                      onChange={handleInputChange}
                      error={!!errors.role}
                      helperText={errors.role}
                      margin="normal"
                      variant="outlined"
                      required
                    >
                      <MenuItem value="plaintiff">Potential Plaintiff</MenuItem>
                      <MenuItem value="lawyer">Lawyer/Law Firm</MenuItem>
                      <MenuItem value="partner">Potential Partner</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={4}
                      value={formValues.message}
                      onChange={handleInputChange}
                      error={!!errors.message}
                      helperText={errors.message}
                      margin="normal"
                      variant="outlined"
                      required
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={loading}
                      startIcon={
                        loading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SendIcon />
                        )
                      }
                      sx={{
                        mt: 2,
                        py: 1.5,
                        borderRadius: '10px',
                        fontWeight: 600,
                        textTransform: 'none',
                      }}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default ContactSection;
